import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Copy, Trash2, Loader2, ExternalLink, ArrowRight, ArrowLeft, Phone, Smartphone, Send } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { EmptyState } from '../components/common/EmptyState';
import UpgradePrompt from '../components/common/UpgradePrompt';
import { StatusBadge } from '../components/common/StatusBadge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { getTier1Languages } from '../lib/languages';
import ContactActionsSheet from '../components/ContactActionsSheet';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

/**
 * ClientsPage Component
 * Per PocketFiler UI Style Guide - uses SVG icons from asset pack, no emojis
 */

// SVG Icon component
const SvgIcon = ({ src, className = 'h-5 w-5' }) => (
  <img src={`/assets/${src}`} alt="" className={className} aria-hidden="true" />
);

const contactMethods = [
  { value: 'email', label: 'Email', disabled: false },
  { value: 'sms', label: 'SMS', disabled: true, tooltip: 'Coming Soon' },
  { value: 'both', label: 'Both', disabled: true, tooltip: 'Coming Soon' }
];

export default function ClientsPage() {
  const { t, language } = useTranslation();
  const tier1Languages = getTier1Languages();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Contact, 2: Preview message, 3: Sent
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [createdInviteUrl, setCreatedInviteUrl] = useState('');

  // Contact actions sheet state
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactActionsOpen, setContactActionsOpen] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    preferred_language: language,
    virtual_office_id: '',
    contact_method: 'email',
    custom_note: ''
  });

  const [errors, setErrors] = useState({});

  // Handle clicking on a contact to show actions
  const handleContactClick = (client) => {
    setSelectedContact(client);
    setContactActionsOpen(true);
  };

  // Check if Contact Picker API is available (mobile)
  const hasContactPicker = 'contacts' in navigator && 'ContactsManager' in window;

  // Import contact from phone
  const handleImportFromPhone = async () => {
    if (!('contacts' in navigator)) {
      toast.error('Contact import not supported on this device');
      return;
    }

    try {
      const props = ['name', 'email', 'tel'];
      const opts = { multiple: false };

      const contacts = await navigator.contacts.select(props, opts);

      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        const name = contact.name?.[0] || '';
        const email = contact.email?.[0] || '';
        const phone = contact.tel?.[0] || '';

        // Pre-fill the form
        setFormData(prev => ({
          ...prev,
          name: name,
          contact_email: email,
          contact_phone: phone
        }));

        setDialogOpen(true);
        toast.success(`Imported: ${name || email || phone}`);
      }
    } catch (err) {
      if (err.name !== 'TypeError') {
        console.error('Contact picker error:', err);
        toast.error('Could not access contacts');
      }
    }
  };

  // Auto-open invite dialog if URL param
  useEffect(() => {
    if (searchParams.get('action') === 'invite') {
      const officeId = searchParams.get('office');
      if (officeId) {
        setFormData(prev => ({ ...prev, virtual_office_id: officeId }));
      }
      setDialogOpen(true);
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients')
  });

  const { data: offices } = useQuery({
    queryKey: ['virtual-offices'],
    queryFn: () => api.get('/virtual-offices')
  });

  const { data: orgPlanInfo } = useQuery({
    queryKey: ['org-plan', user?.organization_id],
    queryFn: () => api.get(`/organizations/${user.organization_id}/plan`),
    enabled: !!user?.organization_id
  });

  const hasLimit = true; // All plans check limits (backend enforces)
  const clientLimit = orgPlanInfo?.limits?.max_clients ?? -1;
  const clientCount = clients?.length ?? 0;
  const atClientLimit = hasLimit && clientLimit !== -1 && clientCount >= clientLimit;

  const { data: joinedOrgs, isLoading: isLoadingJoined } = useQuery({
    queryKey: ['clients-joined'],
    queryFn: () => api.get('/clients/joined')
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/clients', data),
    onSuccess: (newClient) => {
      queryClient.invalidateQueries(['clients']);
      toast.success(t('clients.invite.success'));
      const inviteUrl = `${window.location.origin}/invite/${newClient.invite_token}`;
      setCreatedInviteUrl(inviteUrl);
      navigator.clipboard.writeText(inviteUrl);
      setStep(3);
    },
    onError: (error) => {
      toast.error(error.message || t('error.generic'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  });

  const handleDeleteClick = (clientId) => {
    setClientToDelete(clientId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (clientToDelete) {
      deleteMutation.mutate(clientToDelete);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      preferred_language: language,
      virtual_office_id: '',
      contact_method: 'email',
      custom_note: ''
    });
    setErrors({});
    setStep(1);
    setInviteMessage('');
    setCreatedInviteUrl('');
  };

  const handleClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  const copyInviteLink = (token) => {
    const inviteUrl = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success(t('clients.invite.linkCopied'));
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{10,15}$/;

    // Name - Required
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('validation.nameRequired');
    } else if (formData.name.length > 50) {
      newErrors.name = t('validation.nameTooLong');
    }

    // Email Validation (Required if email or both)
    if (formData.contact_method === 'email' || formData.contact_method === 'both') {
      if (!formData.contact_email) {
        newErrors.contact_email = t('validation.emailRequired') || "Email is required";
      } else if (!emailRegex.test(formData.contact_email)) {
        newErrors.contact_email = t('validation.emailInvalid') || "Invalid email format";
      }
    }

    // Phone Validation (Required if sms or both)
    if (formData.contact_method === 'sms' || formData.contact_method === 'both') {
      if (!formData.contact_phone) {
        newErrors.contact_phone = t('validation.phoneRequired') || "Phone number is required";
      } else if (!phoneRegex.test(formData.contact_phone.replace(/\s/g, ''))) {
        newErrors.contact_phone = t('validation.phoneInvalid') || "Invalid phone number (10-15 digits)";
      }
    }

    // Note Validation
    if (formData.custom_note && formData.custom_note.length > 200) {
      newErrors.custom_note = t('validation.noteTooLong') || "Note must be less than 200 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateInviteMessage = async () => {
    if (!validateForm()) {
      return;
    }

    setGeneratingMessage(true);
    try {
      const office = offices?.find(o => o._id === formData.virtual_office_id);
      const response = await api.post('/ai/invite-message', {
        client_name: formData.name || null,
        agent_name: user?.name || t('clients.invite.yourAgent'),
        office_name: office?.name || t('clients.invite.defaultOffice'),
        target_language: formData.preferred_language,
        custom_note: formData.custom_note || null
      });
      setInviteMessage(response.message);
      setStep(2);
    } catch (error) {
      toast.error(t('error.generic'));
    } finally {
      setGeneratingMessage(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Clean up data before sending
    const cleanedData = {
      name: formData.name || null,
      contact_email: formData.contact_email || null,
      contact_phone: formData.contact_phone || null,
      preferred_language: formData.preferred_language,
      virtual_office_id: formData.virtual_office_id === 'none' ? null : formData.virtual_office_id
    };
    createMutation.mutate(cleanedData);
  };

  const selectedOffice = offices?.find(o => o._id === formData.virtual_office_id);

  return (
    <div className="space-y-6 animate-fade-in pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">{t('clients.title')}</h1>
          <p className="text-muted-foreground">{t('clients.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Plan usage badge */}
          {hasLimit && clientLimit !== -1 && (
            <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              {clientCount} / {clientLimit} clients
            </span>
          )}
          {/* Import from Phone - only show on supported devices */}
          {'contacts' in navigator && (
            <Button
              variant="outline"
              onClick={handleImportFromPhone}
              className="hidden sm:flex md:hidden"
              data-testid="import-phone-button"
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Import
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="invite-client-button"
                disabled={atClientLimit}
                title={atClientLimit ? `Client limit reached (${clientLimit} on current plan)` : undefined}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('clients.invite')}
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="p-6 pb-2 flex-shrink-0">
                <DialogTitle>
                  {step === 1 && t('clients.invite.step1Title')}
                  {step === 2 && t('clients.invite.step2Title')}
                  {step === 3 && t('clients.invite.step3Title')}
                </DialogTitle>
                <DialogDescription>
                  {step === 1 && t('clients.invite.step1Desc')}
                  {step === 2 && t('clients.invite.step2Desc')}
                  {step === 3 && t('clients.invite.step3Desc')}
                </DialogDescription>
              </DialogHeader>

              {step === 1 && (
                <>
                  <div className="flex-1 overflow-y-auto px-6 py-2">
                    <div className="space-y-4 py-2">
                      {/* Import from Phone button inside dialog */}
                      {'contacts' in navigator && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={handleImportFromPhone}
                        >
                          <Smartphone className="h-4 w-4 mr-2" />
                          Import from Phone Contacts
                        </Button>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="name" className={errors.name ? "text-destructive" : ""}>
                          {t('clients.invite.nameLabel')} <span className="text-destructive ml-1">*</span>
                        </Label>
                        <Input
                          id="name"
                          placeholder={t('clients.invite.namePlaceholder')}
                          value={formData.name}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, name: e.target.value }));
                            if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                          }}
                          className={errors.name ? "border-destructive" : ""}
                          maxLength={50}
                          data-testid="client-name-input"
                        />
                        {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label>{t('clients.invite.contactMethod')}</Label>
                        <Select
                          value={formData.contact_method}
                          onValueChange={(value) => {
                            setFormData(prev => ({ ...prev, contact_method: value }));
                            setErrors({});
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {contactMethods.map(m => (
                              <SelectItem
                                key={m.value}
                                value={m.value}
                                disabled={m.disabled}
                                title={m.disabled ? m.tooltip : ""}
                              >
                                {m.label} {m.disabled && "(Coming Soon)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {(formData.contact_method === 'email' || formData.contact_method === 'both') && (
                        <div className="space-y-2">
                          <Label htmlFor="email" className={errors.contact_email ? "text-destructive" : ""}>
                            {t('auth.login.email')} <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="client@example.com"
                            value={formData.contact_email}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, contact_email: e.target.value }));
                              if (errors.contact_email) setErrors(prev => ({ ...prev, contact_email: null }));
                            }}
                            className={errors.contact_email ? "border-destructive" : ""}
                            data-testid="client-email-input"
                          />
                          {errors.contact_email && <p className="text-xs text-destructive mt-1">{errors.contact_email}</p>}
                        </div>
                      )}

                      {(formData.contact_method === 'sms' || formData.contact_method === 'both') && (
                        <div className="space-y-2">
                          <Label htmlFor="phone" className={errors.contact_phone ? "text-destructive" : ""}>
                            {t('clients.invite.phone')} <span className="text-destructive ml-1">*</span>
                          </Label>
                          <Input
                            id="phone"
                            placeholder="+1 234 567 8900"
                            value={formData.contact_phone}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, contact_phone: e.target.value }));
                              if (errors.contact_phone) setErrors(prev => ({ ...prev, contact_phone: null }));
                            }}
                            className={errors.contact_phone ? "border-destructive" : ""}
                          />
                          {errors.contact_phone && <p className="text-xs text-destructive mt-1">{errors.contact_phone}</p>}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('settings.profile.language')}</Label>
                          <Select
                            value={formData.preferred_language}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_language: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {tier1Languages.map((lang) => (
                                <SelectItem key={lang.code} value={lang.code}>
                                  {lang.nativeName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('offices.title')}</Label>
                          <Select
                            value={formData.virtual_office_id}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, virtual_office_id: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('common.search')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('common.optional')}</SelectItem>
                              {offices?.map(office => (
                                <SelectItem key={office._id} value={office._id}>{office.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="note" className={errors.custom_note ? "text-destructive" : ""}>
                          {t('clients.invite.customNote')} ({t('common.optional')})
                        </Label>
                        <Textarea
                          id="note"
                          placeholder={t('clients.invite.customNotePlaceholder')}
                          value={formData.custom_note}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, custom_note: e.target.value }));
                            if (errors.custom_note) setErrors(prev => ({ ...prev, custom_note: null }));
                          }}
                          rows={2}
                          maxLength={200}
                          className={errors.custom_note ? "border-destructive" : ""}
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          {errors.custom_note ? <span className="text-destructive">{errors.custom_note}</span> : <span></span>}
                          <span>{formData.custom_note.length}/200</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                    <DialogFooter className="pt-2 gap-2">
                      <Button type="button" variant="outline" onClick={handleClose}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={generateInviteMessage}
                        disabled={generatingMessage}
                      >
                        {generatingMessage ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t('common.loading')}
                          </>
                        ) : (
                          <>
                            <SvgIcon src="bulb.svg" className="h-4 w-4 mr-2" />
                            {t('clients.invite.previewMessage')}
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </div>
                </>
              )}

              {step === 2 && (
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                  <div className="flex-1 overflow-y-auto px-6 py-2">
                    <div className="space-y-4 py-2">
                      <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                        <div className="flex items-center gap-2 text-sm text-accent mb-2">
                          <SvgIcon src="bulb.svg" className="h-4 w-4" />
                          {t('clients.invite.aiGenerated')} ({formData.preferred_language.toUpperCase()})
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('clients.invite.messagePreview')}</Label>
                        <Textarea
                          value={inviteMessage}
                          onChange={(e) => setInviteMessage(e.target.value)}
                          rows={4}
                          className="resize-none"
                        />
                        <p className="text-xs text-muted-foreground">{t('clients.invite.messageEditHint')}</p>
                      </div>

                      {selectedOffice && (
                        <div className="p-3 rounded-lg bg-muted">
                          <p className="text-xs text-muted-foreground mb-1">{t('clients.invite.documentsRequested')}:</p>
                          <ul className="text-sm space-y-1">
                            {selectedOffice.document_checklist?.slice(0, 3).map((doc, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                {doc}
                              </li>
                            ))}
                            {(selectedOffice.document_checklist?.length || 0) > 3 && (
                              <li className="text-muted-foreground">+{selectedOffice.document_checklist.length - 3} {t('common.viewAll').toLowerCase()}</li>
                            )}
                          </ul>
                        </div>
                      )}

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={generateInviteMessage}
                        disabled={generatingMessage}
                      >
                        <SvgIcon src="bulb.svg" className="h-4 w-4 mr-1" />
                        {t('clients.invite.regenerate')}
                      </Button>
                    </div>
                  </div>
                  <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                    <DialogFooter className="pt-2 gap-2">
                      <Button type="button" variant="outline" onClick={() => setStep(1)}>
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        {t('common.back')}
                      </Button>
                      <Button type="submit" disabled={createMutation.isPending}>
                        {createMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t('common.loading')}
                          </>
                        ) : (
                          <>
                            {t('clients.invite.sendInvite')}
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </div>
                </form>
              )}

              {step === 3 && (
                <>
                  <div className="flex-1 overflow-y-auto px-6 py-2">
                    <div className="space-y-4 py-4 text-center">
                      <div className="w-16 h-16 mx-auto rounded-full bg-success/10 flex items-center justify-center">
                        <SvgIcon src="goal.svg" className="h-8 w-8" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{t('clients.invite.linkCreated')}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('clients.invite.linkCopiedToClipboard')}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted font-mono text-sm break-all">
                        {createdInviteUrl}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('clients.invite.shareLink')}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
                    <DialogFooter className="pt-2 gap-2">
                      <Button variant="outline" onClick={handleClose}>
                        {t('common.close')}
                      </Button>
                      <Button onClick={() => { resetForm(); }}>
                        <Plus className="h-4 w-4 mr-1" />
                        {t('clients.invite.inviteAnother')}
                      </Button>
                    </DialogFooter>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Plan upgrade prompt when at limit */}
      {atClientLimit && (
        <UpgradePrompt
          feature="Adding more clients"
          message={`You've reached the ${clientLimit}-client limit on your current plan. Upgrade for more.`}
        />
      )}

      {/* Tabs Interface */}
      <Tabs defaultValue="clients" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px] mb-4">
          <TabsTrigger value="clients">{t('clients.nav.myClients') || 'My Clients'}</TabsTrigger>
          <TabsTrigger value="joined">{t('clients.nav.joinedOrgs') || 'Joined Organizations'}</TabsTrigger>
        </TabsList>

        {/* Tab: My Clients */}
        <TabsContent value="clients">
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-6 w-20" />
                    </div>
                  ))}
                </div>
              ) : clients && clients.length > 0 ? (
                <div className="overflow-x-auto">
                <Table className="table-fixed w-full min-w-[700px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-left w-[20%] pl-10">{t('clients.table.client')}</TableHead>
                      <TableHead className="text-center w-[20%]">{t('auth.login.email')}</TableHead>
                      <TableHead className="text-center w-[20%]">{t('clients.table.office')}</TableHead>
                      <TableHead className="text-left w-[10%]">{t('settings.profile.language')}</TableHead>
                      <TableHead className="text-center w-[15%]">{t('clients.table.status')}</TableHead>
                      <TableHead className="text-center w-[15%]">{t('clients.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map(client => {
                      const office = offices?.find(o => o._id === client.virtual_office_id);
                      return (
                        <TableRow
                          key={client._id}
                          data-testid="client-row"
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleContactClick(client)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 flex-shrink-0 rounded-full bg-accent/10 flex items-center justify-center">
                                <span className="font-semibold text-accent">
                                  {client.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-medium hover:text-primary block truncate">
                                      {client.name || 'Unnamed'}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px] break-words">
                                    {client.name || 'Unnamed'}
                                  </TooltipContent>
                                </Tooltip>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(client.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle text-center">
                            <div className="space-y-1 min-w-0 w-full">
                              {client.contact_email && (
                                <div className="flex items-center gap-1 text-sm min-w-0 w-full">
                                  {/* <SvgIcon src="chat.svg" className="h-3 w-3 flex-shrink-0 opacity-50" /> */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="truncate min-w-0 flex-1 text-center">
                                        {client.contact_email}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-[280px] break-words">
                                      {client.contact_email}
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              )}
                              {client.contact_phone && (
                                <div className="flex items-center justify-center gap-1 text-sm w-full">
                                  <SvgIcon src="phone call.svg" className="h-3 w-3 opacity-50" />
                                  {client.contact_phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {office ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-sm block truncate">
                                    {office.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] break-words">
                                  {office.name}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="language-chip">
                              {client.preferred_language?.toUpperCase() || 'EN'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <StatusBadge status={client.invite_status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleContactClick(client)}
                                aria-label="Contact options"
                                title="Contact options"
                                className="text-primary"
                              >
                                <Phone className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyInviteLink(client.invite_token)}
                                aria-label="Copy invite link"
                                title="Copy invite link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(`/invite/${client.invite_token}`, '_blank')}
                                aria-label="Open invite page"
                                title="Open invite page"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteClick(client._id)}
                                aria-label="Delete client"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="p-6">
                  <EmptyState
                    svgIcon="user.svg"
                    title={t('clients.empty.title')}
                    description={t('clients.empty.desc')}
                    action={
                      <Button onClick={() => setDialogOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('clients.invite')}
                      </Button>
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Joined Organizations */}
        <TabsContent value="joined">
          <Card>
            <CardContent className="p-0">
              {isLoadingJoined ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))}
                </div>
              ) : joinedOrgs && joinedOrgs.length > 0 ? (
                <div className="overflow-x-auto">
                <Table className="table-fixed w-full min-w-[500px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%] pl-10">{t('clients.table.organization') || 'Organization'}</TableHead>
                      <TableHead className="text-center w-[30%]">{t('auth.login.email')}</TableHead>
                      <TableHead className="w-[20%]">{t('clients.table.joinedDate') || 'Joined Date'}</TableHead>
                      <TableHead className="w-[20%]">{t('clients.table.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {joinedOrgs.map(org => (
                      <TableRow key={org._id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="font-semibold text-primary">
                                {org.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-medium block truncate">
                                    {org.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] break-words">
                                  {org.name}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-middle text-center">
                          {org.contact_email ? (
                            <div className="flex items-center gap-1 text-sm min-w-0 w-full">
                              {/* <SvgIcon src="chat.svg" className="h-3 w-3 flex-shrink-0 opacity-50" /> */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate min-w-0 flex-1 text-center">
                                    {org.contact_email}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] break-words">
                                  {org.contact_email}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {new Date(org.joined_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status="active" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="p-6">
                  <EmptyState
                    svgIcon="organization.svg"
                    title={t('clients.joined.empty.title') || "No Organizations Joined"}
                    description={t('clients.joined.empty.desc') || "You haven't joined any organizations yet."}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteConfirmTitle') || "Are you absolutely sure?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.deleteConfirmDesc') || "This action cannot be undone. This will permanently delete the client and remove their data from our servers."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contact Actions Sheet */}
      <ContactActionsSheet
        open={contactActionsOpen}
        onOpenChange={setContactActionsOpen}
        contact={selectedContact}
      />
    </div>
  );
}
